// src/context/UploadManagerContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import axios from "axios";
import {
  savePendingFiles,
  loadPendingFiles,
  clearPendingFiles,
  savePendingMeta,
  loadPendingMeta,
  clearPendingMeta,
} from "./uploadPersistence";

const UploadManagerContext = createContext(null);

export const useUploadManager = () => {
  const ctx = useContext(UploadManagerContext);
  if (!ctx) {
    throw new Error("useUploadManager must be used inside <UploadManagerProvider>");
  }
  return ctx;
};

export const UploadManagerProvider = ({ children }) => {
  // items: [{ id, name, size, loaded, status, error, caseId, kind }]
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("upload");
  const [startedAt, setStartedAt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [batchCaseId, setBatchCaseId] = useState(null);

  // Refs (survive re-renders and navigation)
  const abortRef = useRef(null);
  const lastProgressRef = useRef({});
  const busyRef = useRef(false);
  const closeTimerRef = useRef(null);

  // Ref to hold the latest buildRequest per file id (used for resume)
  const resumePlanRef = useRef(null);

  // ---- Auto-cleanup on unmount (only aborts, does NOT clear persistence) ----
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // ---- beforeunload: mark in-flight batch as interrupted so next load resumes ----
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only persist if an upload batch is genuinely in progress
      if (!busyRef.current) return;
      const meta = loadPendingMeta();
      if (meta) {
        meta.interrupted = true;
        meta.interruptedAt = Date.now();
        savePendingMeta(meta);
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const patchItem = useCallback((id, patch) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  }, []);

  const getErrorMessage = useCallback((err) => {
    if (err.response) {
      if (err.response.data?.message) return err.response.data.message;
      if (err.response.status === 413) return "File too large for the server";
      return `Server error (${err.response.status})`;
    }
    if (err.code === "ECONNABORTED") return "Request timed out. Please try again.";
    return err.message || "Upload failed";
  }, []);

  const cancel = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    // User explicitly canceled — do NOT resume next time.
    clearPendingFiles();
    clearPendingMeta();
  }, []);

  /**
   * Core runner. Shared by startBatch() and resumePendingBatch().
   * @param {File[]} files
   * @param {'upload'|'replace'} kindArg
   * @param {(file: File, index: number) => { method, url, field, caseId }} buildRequest
   * @param {number} concurrency
   * @param {object} [opts]
   * @param {string[]} [opts.persistedIds] IDs pre-assigned (used on resume)
   * @param {boolean} [opts.persist] Whether to save files to IndexedDB
   */
  const runBatch = useCallback(
    async (files, kindArg, buildRequest, concurrency = 2, opts = {}) => {
      const { persistedIds = null, persist = true } = opts;

      if (busyRef.current) {
        return { done: 0, failed: 0, canceled: 0, lastError: "Busy" };
      }

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      busyRef.current = true;
      setBusy(true);

      const controller = new AbortController();
      abortRef.current = controller;
      lastProgressRef.current = {};

      const stamp = Date.now();
      const newItems = files.map((file, idx) => ({
        id: persistedIds?.[idx] || `${stamp}-${idx}`,
        name: file.name,
        size: file.size,
        loaded: 0,
        status: "queued",
        error: "",
      }));

      setItems(newItems);
      setKind(kindArg);
      setStartedAt(stamp);

      // Build the request-descriptor list once so we can persist it.
      const firstCaseId = (() => {
        try {
          return buildRequest(files[0], 0)?.caseId ?? null;
        } catch {
          return null;
        }
      })();
      setBatchCaseId(firstCaseId);

      // ---- Persist queue metadata + files to IndexedDB ----
      if (persist) {
        savePendingMeta({
          kind: kindArg,
          caseId: firstCaseId,
          startedAt: stamp,
          ids: newItems.map((i) => i.id),
          names: newItems.map((i) => i.name),
          sizes: newItems.map((i) => i.size),
          interrupted: false,
        });

        // Build a descriptor template per file (URL, method, field) so that on
        // reload we can rebuild requests without needing the original closure.
        const descriptors = files.map((f, idx) => {
          try {
            const r = buildRequest(f, idx);
            return { method: r.method, url: r.url, field: r.field, caseId: r.caseId };
          } catch {
            return null;
          }
        });

        savePendingMeta({
          kind: kindArg,
          caseId: firstCaseId,
          startedAt: stamp,
          ids: newItems.map((i) => i.id),
          names: newItems.map((i) => i.name),
          sizes: newItems.map((i) => i.size),
          descriptors,
          interrupted: false,
        });

        // Save the raw Files into IndexedDB
        await savePendingFiles(
          files.map((file, idx) => ({ id: newItems[idx].id, file }))
        );
      }

      const results = { done: 0, failed: 0, canceled: 0, lastError: "" };
      let cursor = 0;

      const worker = async () => {
        while (!controller.signal.aborted) {
          const idx = cursor++;
          if (idx >= files.length) return;

          const file = files[idx];
          const id = newItems[idx].id;
          const { method, url, field } = buildRequest(file, idx);

          const formData = new FormData();
          formData.append(field, file);

          patchItem(id, { status: "uploading" });

          try {
            const response = await axios({
              method,
              url,
              data: formData,
              withCredentials: true,
              signal: controller.signal,
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: (e) => {
                if (!e.total) return;
                const now = Date.now();
                const last = lastProgressRef.current[id] || 0;
                const complete = e.loaded >= e.total;
                if (!complete && now - last < 80) return;
                lastProgressRef.current[id] = now;
                patchItem(id, {
                  loaded: complete
                    ? file.size
                    : Math.round((e.loaded / e.total) * file.size),
                });
              },
            });

            if (response.data && response.data.success === false) {
              throw new Error(response.data.message || "Upload failed");
            }

            patchItem(id, { status: "done", loaded: file.size });
            results.done += 1;

            // Remove this file from persistence once it succeeds
            if (persist) {
              await removePendingFile(id);
            }
          } catch (err) {
            if (axios.isCancel(err) || controller.signal.aborted) {
              patchItem(id, { status: "canceled" });
              results.canceled += 1;
            } else {
              const message = getErrorMessage(err);
              // eslint-disable-next-line no-console
              console.error("Upload error:", err);
              patchItem(id, { status: "error", error: message });
              results.failed += 1;
              results.lastError = message;
              // Leave file in IndexedDB so a manual retry can pick it up
            }
          }
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(concurrency, files.length) }, worker)
      );

      setItems((prev) =>
        prev.map((item) =>
          item.status === "queued" ? { ...item, status: "canceled" } : item
        )
      );
      results.canceled += Math.max(
        0,
        files.length - results.done - results.failed - results.canceled
      );

      abortRef.current = null;
      busyRef.current = false;
      setBusy(false);

      // Auto-clear successful batches after a short delay
      if (results.failed === 0 && results.canceled === 0) {
        if (persist) {
          await clearPendingFiles();
          clearPendingMeta();
        }
        closeTimerRef.current = setTimeout(() => {
          setItems([]);
          setBatchCaseId(null);
          closeTimerRef.current = null;
        }, 3000);
      } else {
        // Keep meta (marked complete) so no auto-resume next time,
        // but files remain in IDB until user dismisses.
        if (persist) {
          const meta = loadPendingMeta();
          if (meta) {
            meta.interrupted = false;
            meta.finishedAt = Date.now();
            savePendingMeta(meta);
          }
        }
      }

      return results;
    },
    [patchItem, getErrorMessage]
  );

  // ---- Public API: start a new batch ----
  const startBatch = useCallback(
    (files, kindArg, buildRequest, concurrency = 2) =>
      runBatch(files, kindArg, buildRequest, concurrency, {
        persistedIds: null,
        persist: true,
      }),
    [runBatch]
  );

  // ---- Public API: resume whatever was in-flight before reload ----
  const resumePendingBatch = useCallback(async () => {
    const meta = loadPendingMeta();
    if (!meta || !meta.interrupted) return null;
    if (busyRef.current) return null;

    const records = await loadPendingFiles();
    if (!records || records.length === 0) {
      clearPendingMeta();
      return null;
    }

    // Only resume files that were not completed.
    // meta.ids align 1:1 with meta.descriptors.
    const ids = meta.ids || [];
    const descriptors = meta.descriptors || [];
    const pairs = records
      .map((rec) => {
        const idx = ids.indexOf(rec.id);
        return { rec, idx, desc: descriptors[idx] };
      })
      .filter((p) => p.desc && p.rec.file instanceof File);

    if (pairs.length === 0) {
      clearPendingMeta();
      await clearPendingFiles();
      return null;
    }

    const files = pairs.map((p) => p.rec.file);
    const persistedIds = pairs.map((p) => p.rec.id);

    // Rebuild the buildRequest function from stored descriptors.
    const buildRequest = (file, idx) => {
      const d = pairs[idx].desc;
      return {
        method: d.method,
        url: d.url,
        field: d.field,
        caseId: d.caseId,
      };
    };

    // eslint-disable-next-line no-console
    console.info(
      `[UploadManager] Resuming ${files.length} interrupted upload(s)...`
    );

    return runBatch(files, meta.kind || "upload", buildRequest, 2, {
      persistedIds,
      persist: true,
    });
  }, [runBatch]);

  const dismiss = useCallback(async () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setItems([]);
    setBatchCaseId(null);
    await clearPendingFiles();
    clearPendingMeta();
  }, []);

  // ---- Auto-resume on first mount ----
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;

    // Slight delay so the app tree is mounted
    const t = setTimeout(() => {
      resumePendingBatch().catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[UploadManager] auto-resume failed:", err);
      });
    }, 500);
    return () => clearTimeout(t);
  }, [resumePendingBatch]);

  const value = {
    items,
    kind,
    startedAt,
    batchCaseId,
    busy,
    startBatch,
    cancel,
    dismiss,
    isUploading: items.some(
      (i) => i.status === "queued" || i.status === "uploading"
    ),
  };

  return (
    <UploadManagerContext.Provider value={value}>
      {children}
    </UploadManagerContext.Provider>
  );
};

// ---- helpers ----
async function removePendingFile(id) {
  try {
    const { default: _ } = { default: null };
    // Import here to avoid circular deps
    const mod = await import("./uploadPersistence");
    // No direct delete in the wrapper — easiest way is to re-save minus this id.
    // For small batches this is fine.
    const remaining = (await mod.loadPendingFiles()).filter((r) => r.id !== id);
    await mod.savePendingFiles(remaining);
  } catch {
    /* ignore */
  }
}